import * as React from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";

import { Button } from "@/components/shadcn/button.tsx";
import Branding from "@/utils/branding.ts";
import { useContext, useState } from "react";
import {Icon} from "@/components/Icon.tsx";
import { useTranslation } from "react-i18next";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";
import Loading from "@/components/Loading";
import { TimezoneSelect } from "@/components/shadcn-custom/timezone-select.tsx";

const lang = Branding().language;

const hours: number[] = [];
for (let i = 0; i < 24; i += 1) {
  hours.push(i);
}
const minutes: number[] = [];
for (let i = 0; i < 60; i += 5) {
  minutes.push(i);
}

function weekdayName(day: number) {
  return new Intl.DateTimeFormat(lang, { weekday: "long" }).format(
    (4 + day) * 864e5,
  );
}

const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export interface WorkScheduleSettings {
  ranges: timeRange[];
  timezone: string;
}

interface timeRange {
  weekDay: number;
  startHour: number;
  endHour: number;
  startMinute: number;
  endMinute: number;
}

const rangeID = function (range: timeRange): string {
  return `${range.weekDay};${range.startHour}:${range.startMinute}-${range.endHour}:${range.endMinute}`;
};

const TimePicker = function ({
  hour,
  min,
  onUpdate,
}: {
  hour: number;
  min: number;
  onUpdate: (hour: number, min: number) => void;
}) {
  const [hoursOpened, setHoursOpened] = useState(false);
  const [minutesOpened, setMinutesOpened] = useState(false);

  return (
    <div className="flex">
      <Select
        onOpenChange={(opened: boolean) => {
          opened && setHoursOpened(opened);
        }}
        onValueChange={(value) => {
          onUpdate(parseInt(value), min);
        }}
      >
        <SelectTrigger className="w-[60px] mr-1">
          <SelectValue placeholder={hour < 10 ? "0" + hour : hour} />
        </SelectTrigger>
        <SelectContent>
          {hoursOpened && (
            <SelectGroup>
              {hours.map((hour, i) => (
                <SelectItem key={i} value={hour}>
                  {hour < 10 ? "0" + hour : hour}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
      <Select
        onOpenChange={(opened: boolean) => {
          opened && setMinutesOpened(opened);
        }}
        onValueChange={(value) => {
          onUpdate(hour, parseInt(value));
        }}
      >
        <SelectTrigger className="w-[60px]">
          <SelectValue placeholder={min < 10 ? "0" + min : min} />
        </SelectTrigger>
        <SelectContent>
          {minutesOpened && (
            <SelectGroup>
              {minutes.map((minutes, i) => (
                <SelectItem key={i} value={minutes}>
                  {minutes < 10 ? "0" + minutes : minutes}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

export function WorkSchedule() {
  const { t } = useTranslation();
  const { chatSettings, viewChatbot, invalidateChatbot, setSettings } =
    useContext(ChatbotContext);

  const getSchedule = (): timeRange[] => {
    return chatSettings?.schedule.ranges ? chatSettings?.schedule.ranges : [];
  };

  const setSchedule = (list: timeRange[]) => {
    if (!chatSettings) {
      return;
    }

    setSettings({
      ...chatSettings,
      schedule: {
        ranges: list,
        timezone: chatSettings?.schedule.timezone,
      },
    });
  };

  const updateRange = (findRange: timeRange, newRange: timeRange) => {
    let id = rangeID(findRange);

    let newList = [...getSchedule()];
    let i = newList.findIndex((item: timeRange) => {
      return rangeID(item) === id;
    });
    if (i !== -1) {
      newList[i] = newRange;
    }
    setSchedule(newList);
  };

  const deleteRange = (findRange: timeRange) => {
    let id = rangeID(findRange);
    setSchedule(
      getSchedule().filter((item: timeRange) => {
        return rangeID(item) !== id;
      }),
    );
  };

  const TimeRangePicker = function ({ range }: { range: timeRange }) {
    return (
      <div className="flex items-center p-1 bg-slate-100 rounded-lg w-[305px]">
        <TimePicker
          hour={range.startHour}
          min={range.startMinute}
          onUpdate={(hour: number, min: number) => {
            updateRange(range, {
              weekDay: range.weekDay,
              startHour: hour,
              startMinute: min,
              endHour: range.endHour,
              endMinute: range.endMinute,
            });
          }}
        />
        <div className="px-1">-</div>
        <TimePicker
          hour={range.endHour}
          min={range.endMinute}
          onUpdate={(hour: number, min: number) => {
            updateRange(range, {
              weekDay: range.weekDay,
              startHour: range.startHour,
              startMinute: range.startMinute,
              endHour: hour,
              endMinute: min,
            });
          }}
        />
        <div
          className="px-2 opacity-50 hover:opacity-100 cursor-pointer"
          onClick={() => {
            deleteRange(range);
          }}
        >
          <Icon icon="fluent:dismiss-16-regular" width="16" />
        </div>
      </div>
    );
  };

  if (!chatSettings || !viewChatbot) {
    return <Loading />;
  }

  return (
    <>
      <div className="mb-6">
        <label className="block capitalize form-label items-center">
          {t("timezone")}
        </label>
        <TimezoneSelect
          timezone={chatSettings.schedule.timezone}
          onUpdate={(timezone: string) => {
            let set = { ...chatSettings };
            set.schedule.timezone = timezone;
            setSettings(set);
          }}
        />
      </div>

      {weekDays.map((day: string, weekDayNumber: number) => {
        let ranges = getSchedule().filter(
          (item: timeRange) => item.weekDay === weekDayNumber + 1,
        );

        return (
          <div key={weekDayNumber} className="py-2">
            <span className="inline-block min-w-[140px]">
              {weekdayName(weekDayNumber)}
            </span>
            <div className="grid grid-cols-3 gap-1 items-center">
              {ranges.map((item: timeRange, i) => (
                <TimeRangePicker range={item} key={i} />
              ))}
              <Button
                className={`w-10 ${ranges.length ? `ml-2` : ""}`}
                variant="outline"
                onClick={() => {
                  setSchedule([
                    ...getSchedule(),
                    {
                      weekDay: weekDayNumber + 1,
                      startHour: 9,
                      startMinute: 0,
                      endHour: 18,
                      endMinute: 0,
                    },
                  ]);
                }}
              >
                +
              </Button>
            </div>
          </div>
        );
      })}
    </>
  );
}
